import React from "react";
import classNames from "classnames";
import { LoadingOutlined } from "@ant-design/icons";
import styles from "./CustomButton.module.css";

export interface CustomButtonProps {
  /**
   * Click handler
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Button content
   */
  children?: React.ReactNode;
  /**
   * Background color
   * @default 'var(--theme-primary)' (Theme Teal)
   */
  bgColor?: string;
  /**
   * Text color
   * @default '#FFFFFF'
   */
  color?: string;
  /**
   * Optional icon
   */
  icon?: React.ReactNode;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Inline style
   */
  style?: React.CSSProperties;
  /**
   * Whether to fit width to parent
   * @default false
   */
  block?: boolean;
  /**
   * Button height
   * @default '32px'
   */
  height?: string | number;
  /**
   * Font size
   * @default '18px'
   */
  fontSize?: string | number;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Disabled state
   */
  disabled?: boolean;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  onClick,
  children,
  bgColor = "var(--theme-primary)", // Default based on the image provided (looks like a teal/cyan color)
  color = "#FFFFFF",
  icon,
  className,
  style,
  block = false,
  height = "32px",
  fontSize = "16px",
  loading = false,
  disabled = false,
}) => {
  // Calculate width: prop > default (if not block). Block overrides everything to 100%.
  // However, user might want block behavior but specific width? Usually block means width: 100%.
  // If block is true, we let CSS handle width (100%).
  // If block is false, we use provided width or default fit-content.

  const computedWidth = block ? undefined : "fit-content";

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (loading || disabled) return;
    onClick?.(e);
  };

  return (
    <div
      className={classNames(
        styles.button,
        {
          [styles.block]: block,
          [styles.disabled]: disabled || loading,
        },
        className
      )}
      style={{
        backgroundColor: bgColor,
        color: color,
        width: computedWidth,
        height: height,
        fontSize: fontSize,
        opacity: disabled || loading ? 0.6 : 1,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        ...style,
      }}
      onClick={handleClick}
      role="button"
      tabIndex={disabled || loading ? -1 : 0}
    >
      {loading && <LoadingOutlined style={{ marginRight: 8 }} spin />}
      {!loading && icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </div>
  );
};

export default CustomButton;
